import {isPlaneteer} from '@apps-common/auth-utils/util.js';
import {type User} from '@apps-common/auth/client';
import {isTrialUser} from '@src/utils/user.js';

// A canonical list of individual lab keys.
const SATELLITE_ID_FILTER = 'satelliteIdFilter';
const NEW_API_DIALOG = 'newApiDialog';
const WEBP_MOSAIC_TILES = 'webPMosaicTiles';
const SNAPSHOT = 'snapshot';
const RESULT_SCENE_SHARE_MENU = 'sceneShareMenu';
const ORDERS_SHOW_PARTIAL = 'ordersShowPartial';
const ORDERS_MULTI_SELECT = 'ordersMultiSelect';

// Export object of Lab keys
export const LABS_MAP = {
  SATELLITE_ID_FILTER,
  NEW_API_DIALOG,
  WEBP_MOSAIC_TILES,
  SNAPSHOT,
  RESULT_SCENE_SHARE_MENU,
  ORDERS_SHOW_PARTIAL,
  ORDERS_MULTI_SELECT,
} as const;

/**
 * Configs for labs
 * available - will be used to filter labs if not defined then it is available to all
 * private - adds Planeteer Only tag
 * @param User user
 */
const getLabs = (user: User) => {
  const userIsTrial = isTrialUser(user.claims);
  const planeteer = isPlaneteer(user.claims.email);

  return [
    {
      id: LABS_MAP.SATELLITE_ID_FILTER,
      name: 'Satellite Id Filter',
      description:
        'Displays a new filter that allows filtering by satellite id',
      available: planeteer,
      private: true,
    },
    {
      id: LABS_MAP.NEW_API_DIALOG,
      name: 'API Dialog Extras',
      description: 'Show XYZ and WMTS layer URLs',
      available: planeteer,
      private: true,
    },
    {
      id: LABS_MAP.SNAPSHOT,
      name: '📷 Snapshot',
      description: 'Download snapshot images',
      available: !userIsTrial,
    },
    {
      id: LABS_MAP.RESULT_SCENE_SHARE_MENU,
      name: 'Result Scenes Share Menu',
      description:
        'Use an alternate menu for result scenes that allows creating a shareable link',
      available: planeteer,
      private: true,
    },
    {
      id: LABS_MAP.ORDERS_SHOW_PARTIAL,
      name: 'Orders Dialog - Show full menu of assets',
      description: `Use an alternate Orders dialog that shows the full menu of assets, even if the chosen asset is not available for all selected imagery.`,
      available: planeteer,
      private: true,
    },
    {
      id: LABS_MAP.ORDERS_MULTI_SELECT,
      name: 'Orders Dialog - Allow selecting multiple assets',
      description: `Allows ordering of multiple assets per item type.`,
      available: planeteer,
      private: true,
    },
  ];
};

export const LABS_DEFAULT = {} as const;

/**
 * Get the filtered list of labs for the user
 * @param {} user
 */
export const getUserLabs = (user: User) => {
  const labs = getLabs(user).filter(lab =>
    lab.available === undefined ? true : lab.available,
  );

  return labs;
};

export const getUserLabById = (user: User, id: string) =>
  getUserLabs(user).filter(lab => lab.id === id)[0];

/**
 * Just return the keys - mainly used in app.js
 * @param {} user
 */
export const getUserLabKeys = (user: User) => {
  return getUserLabs(user).map(lab => lab.id);
};
